import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, ShieldCheck, BarChart3, Megaphone, Sparkles, Wrench, X } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { useCookieConsent } from '../context/CookieConsentContext';

const CATEGORIES = [
  {
    key: 'essential',
    title: 'Strictly Necessary',
    locked: true,
    Icon: ShieldCheck,
    description:
      'Required for the website to function — secure login (httpOnly session cookies), cart, checkout, and security. These cannot be turned off.',
  },
  {
    key: 'functional',
    title: 'Functional',
    locked: false,
    Icon: Wrench,
    description:
      'Remembers your preferences such as language, region and live chat session continuity for a smoother experience.',
  },
  {
    key: 'analytics',
    title: 'Analytics & Performance',
    locked: false,
    Icon: BarChart3,
    description:
      'Helps us understand how visitors interact with Daye Farm Ltd so we can improve the site (aggregated, never sold).',
  },
  {
    key: 'personalization',
    title: 'Personalization',
    locked: false,
    Icon: Sparkles,
    description:
      'Tailors product recommendations, content and offers to your interests across the Farmers Market and Investment portal.',
  },
  {
    key: 'advertising',
    title: 'Advertising',
    locked: false,
    Icon: Megaphone,
    description:
      'Allows us and trusted partners to measure ad performance and deliver more relevant marketing for our farm products.',
  },
];

const CookieConsent = () => {
  const {
    preferences,
    showBanner,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useCookieConsent();

  // Local draft state for the preferences modal
  const [draft, setDraft] = useState(preferences);

  useEffect(() => {
    if (showPreferences) {
      setDraft(preferences);
    }
  }, [showPreferences, preferences]);

  const toggle = (key) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    savePreferences(draft);
  };

  return (
    <>
      {/* Floating bottom banner */}
      {showBanner && !showPreferences && (
        <div
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 sm:pb-6 animate-in slide-in-from-bottom-8 duration-500"
        >
          <div className="max-w-6xl mx-auto bg-white border border-gray-200 shadow-2xl rounded-2xl overflow-hidden">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 p-5 sm:p-6">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                  <Cookie className="h-5 w-5 text-green-700" />
                </div>
                <div className="text-sm text-gray-700 leading-relaxed">
                  <p className="font-semibold text-gray-900 mb-1">
                    We value your privacy
                  </p>
                  <p>
                    We use cookies to keep our site secure, understand how it is used and improve your
                    experience. You can accept all, reject non-essential, or customize your choices.
                    Read our{' '}
                    <Link to="/legal/cookies" className="text-green-700 hover:underline font-medium">
                      Cookie Notice
                    </Link>{' '}
                    and{' '}
                    <Link to="/legal/privacy" className="text-green-700 hover:underline font-medium">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <Button
                  variant="outline"
                  onClick={openPreferences}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="cookie-customize-btn"
                >
                  <Settings className="h-4 w-4 mr-2" /> Customize
                </Button>
                <Button
                  variant="outline"
                  onClick={rejectAll}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="cookie-reject-btn"
                >
                  Reject Non-Essential
                </Button>
                <Button
                  onClick={acceptAll}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="cookie-accept-btn"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      <Dialog open={showPreferences} onOpenChange={(open) => !open && closePreferences()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="cookie-preferences-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Cookie className="h-6 w-6 text-green-700" /> Cookie Preferences
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Manage how Daye Farm Ltd uses cookies and similar technologies. Your choices are saved
              on this device and can be updated any time from the footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {CATEGORIES.map(({ key, title, locked, Icon, description }) => (
              <div
                key={key}
                className="border border-gray-200 rounded-xl p-4 flex items-start gap-4 hover:border-green-200 transition-colors"
                data-testid={`cookie-category-${key}`}
              >
                <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-green-50 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-green-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">{title}</h4>
                    {locked ? (
                      <span
                        className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full"
                        data-testid={`cookie-toggle-${key}-locked`}
                      >
                        Always On
                      </span>
                    ) : (
                      <Switch
                        checked={!!draft[key]}
                        onCheckedChange={() => toggle(key)}
                        data-testid={`cookie-toggle-${key}`}
                      />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center pt-2">
            <button
              type="button"
              onClick={closePreferences}
              className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
              data-testid="cookie-prefs-close"
            >
              <X className="h-4 w-4" /> Close
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={rejectAll}
                className="border-gray-300"
                data-testid="cookie-prefs-reject-btn"
              >
                Reject Non-Essential
              </Button>
              <Button
                variant="outline"
                onClick={acceptAll}
                className="border-gray-300"
                data-testid="cookie-prefs-accept-all-btn"
              >
                Accept All
              </Button>
              <Button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="cookie-prefs-save-btn"
              >
                Save Preferences
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
